"use client";

import Image from "next/image";
import Link from "next/link";
import { CSSProperties, MouseEventHandler, useMemo, useState } from "react";
import classes from "./RandomBanners.module.css";

import { getSectionStyle } from "@/utils";
import { getPageRoute, getS3ImageUrl } from "@/utils/routes";

interface RandomBannerType {
  banner: string;
  link?: Link;
  title?: string;
  dirs: ["top" | "bottom", "left" | "center" | "right"];
  width?: number;
  height?: number;
  percent?: number;
  noScale?: boolean;
  tall?: boolean;
}

interface Props {
  className?: string;
  style?: CSSProperties;

  widget?: Widget;
}

const RandomBanners = ({
  className,
  style,

  widget,
}: Props) => {
  const classNames = [classes.RandomBanners, className];

  const [activeIndex, setActiveIndex] = useState(0);

  const images = useMemo(
    () => widget?.data?.images || [],
    [widget?.data?.images]
  );

  if (images.length < 5) return null;

  const first2: RandomBannerType[] = [
    {
      banner: images[0].image,
      link: images[0].link,
      title: images[0].title,
      dirs: ["top", "left"],
      width: 800,
      height: 495,
      percent: 100 / (495 / 800),
    },
    {
      banner: images[1].image,
      link: images[1].link,
      title: images[1].title,
      dirs: ["top", "center"],
      width: 900,
      height: 394,
      percent: 100 / (394 / 900),
    },
  ];

  const second2: RandomBannerType[] = [
    {
      banner: images[3].image,
      link: images[3].link,
      title: images[3].title,
      dirs: ["bottom", "left"],
      width: 900,
      height: 395,
      percent: 100 / (395 / 900),
    },
    {
      banner: images[4].image,
      link: images[4].link,
      title: images[4].title,
      dirs: ["bottom", "center"],
      width: 800,
      height: 492,
      percent: 100 / (492 / 800),
    },
  ];

  const third: RandomBannerType = {
    banner: images[2].image,
    link: images[2].link,
    title: images[2].title,
    dirs: ["top", "right"],
    width: 468,
    height: 800,
    noScale: true,
  };

  const settings = widget?.data?.settings || {};

  return (
    <div className={classNames.join(" ")} style={getSectionStyle(settings)}>
      <div className={classes.contents}>
        <div className={classes.left}>
          <div className={classes.top}>
            {first2.map((banner, index) => (
              <RandomBanner
                key={banner.banner || `first-${index}`}
                {...banner}
                active={activeIndex === index}
                onActive={() => setActiveIndex(index)}
              />
            ))}
          </div>

          <div className={classes.bottom}>
            {second2.map((banner, index) => (
              <RandomBanner
                key={banner.banner || `second-${index}`}
                {...banner}
                active={activeIndex === index + 2}
                onActive={() => setActiveIndex(index + 2)}
              />
            ))}
          </div>
        </div>

        <div className={classes.right}>
          <RandomBanner
            key={third.banner || "third-banner"}
            {...third}
            active={activeIndex === 4}
            onActive={() => setActiveIndex(4)}
            tall={true}
          />
        </div>
      </div>
    </div>
  );
};

const RandomBanner = ({
  banner,
  link,
  title,
  dirs,
  width = 500,
  height = 500,
  percent,
  noScale,
  active,
  tall,
  onActive,
}: RandomBannerType & {
  active?: boolean;
  onActive?: () => void;
}) => {
  const [zIndex, setZIndex] = useState(1);

  const onMouseEnter = () => {
    setZIndex(50);
  };

  const onMouseLeave: MouseEventHandler<HTMLAnchorElement> = (e) => {
    const target = e.target as HTMLAnchorElement;

    setZIndex(40);

    target.addEventListener(
      "transitionend",
      () => {
        setZIndex(1);
      },
      { once: true }
    );
  };

  const classNames = `${classes.RandomBanner} ${
    noScale ? classes.noScale : ""
  } ${tall ? classes.tall : ""}`;

  return (
    <Link
      href={getPageRoute(link?.urlType || "custom", link?.url || "")}
      className={classNames}
      style={{
        transformOrigin: dirs.join(" "),
        zIndex: zIndex,
        // ...(percent ? { width: `${percent}%` } : {}),
        width: "100%",
        height: "100%",
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className={classes.banner}>
        <Image
          src={getS3ImageUrl(banner)}
          alt={title || "Banner"}
          unoptimized={true}
          layout="fill"
          // width={width}
          // height={height}
          style={{
            objectFit: "cover",
            objectPosition: "center",
            aspectRatio: width / height,
          }}
        />

        <div className={classes.titleContainer}>
          <h3 className={classes.title}>{title}</h3>
        </div>
      </div>
    </Link>
  );
};

export default RandomBanners;
